import { Controller } from '@hotwired/stimulus'
import Logger from '../lib/logger'

/**
 * QuoteUpdateController
 *
 * Enhances the provider-facing quote update portal with:
 * - Progress tracking (X of Y quotes filled)
 * - "Apply to all" – copy first row's pricing to all rows in a location group
 * - Row completion visual feedback (green checkmark when all required fields filled)
 * - Submit button state (disabled until at least one quote is complete)
 */
export default class extends Controller {
  static targets = ['row', 'progressBar', 'progressText', 'submitBtn', 'applyAllBtn', 'applyAllContainer', 'completeIndicator', 'currencySymbol', 'toggleSavedBtn', 'individualSubmitBtn']
  static values = {
    totalQuotes: { type: Number, default: 0 },
    showSaved: { type: Boolean, default: false }
  }

  connect() {
    Logger.debug('QuoteUpdateController connected', { controller: this.constructor.name })

    // Listen for manual refresh events (e.g. from Turbo Streams)
    this._refreshHandler = () => {
      this.updateProgress()
      this._updateSavedRowsVisibility()
    }
    this.element.addEventListener('quote-update:refresh', this._refreshHandler)

    this.updateProgress()
    this._updateSavedRowsVisibility()

    // Mark as initialized after first run so future calls (like Turbo Streams) trigger animations
    this._initialized = true
  }

  disconnect() {
    Logger.debug('QuoteUpdateController disconnected', { controller: this.constructor.name })
    if (this._refreshHandler) {
      this.element.removeEventListener('quote-update:refresh', this._refreshHandler)
      this._refreshHandler = undefined
    }
  }

  /**
   * Called automatically by Stimulus when a row target is added to the DOM
   * This happens on initial page load AND when a row is replaced via Turbo Stream
   */
  rowTargetConnected(row) {
    // 1. Initialize feasibility state for this row
    const feasibilitySelect = row.querySelector('[data-quote-update-target="feasibility"]')
    if (feasibilitySelect && feasibilitySelect.value === 'negative') {
      this.toggleFeasibility({ target: feasibilitySelect })
    }

    // 2. Initialize formatting for money fields (so "10.5" becomes "10,50")
    const currencySelect = row.querySelector('[name$="[currency]"]')
    const currency = currencySelect ? currencySelect.value : 'BRL'
    const pricingInputs = row.querySelectorAll('[data-quote-update-target="pricingInput"]')

    pricingInputs.forEach(input => {
      if (input.value.trim() !== '') {
        input.value = this._formatCurrencyValue(input.value.trim(), currency)
      }
    })

    // 3. Check initial completion state for the row
    this.checkRow({ target: row })

    // 4. Handle visibility/animation for rows that arrive already saved
    if (row.classList.contains('quote-update-row-saved')) {
      if (!this.showSavedValue && this.totalQuotesValue > 1) {
        if (this._initialized) {
          this._animateRowOut(row)
        } else {
          row.classList.add('d-none')
        }
      }
      this._updateGroupVisibility()
      this._updateToggleButton(this.element.querySelectorAll('.quote-update-row-saved'))
    }
  }

  /**
   * Called on input/change in any form field inside a row.
   * Updates progress bar and row completion indicator.
   */
  checkRow(event) {
    const row = event.target.closest('[data-quote-update-target="row"]')
    if (!row) return

    const isComplete = this._isRowComplete(row)
    row.classList.toggle('quote-update-row-complete', isComplete)
    row.classList.toggle('quote-update-row-incomplete', !isComplete)

    const individualBtn = row.querySelector('[data-quote-update-target="individualSubmitBtn"]')
    if (individualBtn) {
      individualBtn.disabled = !isComplete
    }

    this.updateProgress()
  }

  /**
   * Handle toggling the visibility of already saved (responded) quotes
   */
  toggleSavedRows(event) {
    if (event) event.preventDefault()
    this.showSavedValue = !this.showSavedValue
  }

  showSavedValueChanged() {
    this._updateSavedRowsVisibility()
  }

  _animateRowOut(row) {
    // Prevent double-animating
    if (row.classList.contains('d-none') || row.dataset.animatingOut === 'true') return

    row.dataset.animatingOut = 'true'
    // Remove any conflicting initial page load animation
    row.classList.remove('quote-update-fade-in')

    const startSlideOut = () => {
      // If visibility changed while we were waiting, abort
      if (this.showSavedValue) {
        delete row.dataset.animatingOut
        return
      }

      row.classList.remove('quote-update-saved-flash')
      row.classList.add('quote-update-slide-out')

      const onAnimationEnd = (e) => {
        if (e.animationName !== 'quote-update-slide-out') return
        row.removeEventListener('animationend', onAnimationEnd)
        row.classList.add('d-none')
        row.classList.remove('quote-update-slide-out')
        delete row.dataset.animatingOut
        this._updateGroupVisibility()
      }
      row.addEventListener('animationend', onAnimationEnd)
    }

    if (row.classList.contains('quote-update-saved-flash')) {
      const onFlashEnd = (e) => {
        if (e.animationName !== 'quote-update-saved-flash') return
        row.removeEventListener('animationend', onFlashEnd)
        startSlideOut()
      }
      row.addEventListener('animationend', onFlashEnd)
    } else {
      startSlideOut()
    }
  }

  _updateSavedRowsVisibility() {
    if (this.totalQuotesValue <= 1) return

    const savedRows = this.element.querySelectorAll('.quote-update-row-saved')

    savedRows.forEach(row => {
      if (this.showSavedValue) {
        // Show: remove hidden state and animation classes
        row.classList.remove('d-none', 'quote-update-slide-out')
        delete row.dataset.animatingOut
      } else {
        // If it's already animating out (from a recent submit), let it finish gracefully
        if (row.dataset.animatingOut === 'true') return

        // Otherwise (e.g., clicking "Ocultar Respondidas"), hide it instantly
        row.classList.add('d-none')
        row.classList.remove('quote-update-fade-in')
      }
    })

    this._updateGroupVisibility()
    this._updateToggleButton(savedRows)
  }

  _updateGroupVisibility() {
    const groups = this.element.querySelectorAll('[data-quote-update-group]')
    groups.forEach(group => {
      const allRows = group.querySelectorAll('[data-quote-update-target="row"]')
      if (allRows.length > 0) {
        // A row is only truly invisible once it has d-none. If it is animating out,
        // it must remain in the DOM so the animation can finish.
        const visibleRows = Array.from(allRows).filter(r => !r.classList.contains('d-none'))
        if (visibleRows.length === 0) {
          group.classList.add('d-none')
        } else {
          group.classList.remove('d-none')
        }
      }
    })
  }

  _updateToggleButton(savedRows) {
    if (this.hasToggleSavedBtnTarget) {
      if (savedRows.length > 0) {
        this.toggleSavedBtnTarget.classList.remove('d-none')
        if (this.showSavedValue) {
          this.toggleSavedBtnTarget.innerHTML = '<i class="fas fa-eye-slash me-1"></i> Ocultar Respondidas'
          this.toggleSavedBtnTarget.classList.remove('theme-btn-outline-secondary', 'btn-outline-secondary')
          this.toggleSavedBtnTarget.classList.add('theme-btn-primary', 'text-white')
        } else {
          this.toggleSavedBtnTarget.innerHTML = '<i class="fas fa-eye me-1"></i> Mostrar Respondidas (' + savedRows.length + ')'
          this.toggleSavedBtnTarget.classList.remove('theme-btn-primary', 'text-white')
          this.toggleSavedBtnTarget.classList.add('theme-btn-outline-secondary')
        }
      } else {
        this.toggleSavedBtnTarget.classList.add('d-none')
      }
    }
  }

  /**
   * Handle toggling feasibility state
   */
  toggleFeasibility(event) {
    const isNegative = event.target.value === 'negative'

    // Determine the scope: is this inside a bulk row or single form?
    const row = event.target.closest('[data-quote-update-target="row"]')
    const scope = row || event.target.closest('form')

    if (!scope) return

    // Find all pricing inputs in this scope
    const pricingInputs = scope.querySelectorAll('[data-quote-update-target="pricingInput"]')
    const pricingContainers = scope.querySelectorAll('[data-quote-update-target="pricingContainer"]')

    pricingInputs.forEach(input => {
      input.disabled = isNegative
      input.required = !isNegative
      if (isNegative) {
        input.value = '' // clear value when disabled
      }
    })

    // Hide or show the entire financial section
    const financialSection = scope.querySelector('[data-quote-update-target="financialSection"]')
    if (financialSection) {
      if (isNegative) {
        financialSection.classList.add('d-none')
      } else {
        financialSection.classList.remove('d-none')
      }
    }

    if (scope && scope.classList.contains('quote-update-item-card')) {
      if (isNegative) {
        scope.classList.add('bg-danger', 'bg-opacity-10', 'border-danger')
      } else {
        scope.classList.remove('bg-danger', 'bg-opacity-10', 'border-danger')
      }
    }
  }

  /**
   * Updates the progress bar and text with the count of completed quotes.
   */
  updateProgress() {
    const allRows = this.rowTargets
    const completed = allRows.filter(row => this._isRowComplete(row)).length
    const total = allRows.length

    Logger.debug('Updating progress', { completed, total })

    if (this.hasProgressBarTarget) {
      const percentage = total > 0 ? (completed / total) * 100 : 0
      this.progressBarTarget.ariaValueNow = percentage
      // Clear previous progress classes and apply current one
      this.progressBarTarget.className = this.progressBarTarget.className.replace(/quote-update-progress-\d+/, '')
      this.progressBarTarget.classList.add(`quote-update-progress-${Math.round(percentage / 5) * 5}`)
      this.progressBarTarget.classList.toggle('bg-success', percentage === 100)
    }

    if (this.hasProgressTextTarget) {
      this.progressTextTarget.textContent = `${completed} de ${total} cotações preenchidas`
    }

    if (this.hasSubmitBtnTarget) {
      // Button is enabled ONLY if total > 0 and ALL are completed
      this.submitBtnTarget.disabled = (total === 0 || completed < total)
      this.submitBtnTarget.classList.toggle('quote-update-submit-ready', completed === total && total > 0)
    }

    this._updateApplyAllButtons()
  }

  _updateApplyAllButtons() {
    const groups = this.element.querySelectorAll('[data-quote-update-group]')
    groups.forEach(group => {
      const container = group.querySelector('[data-quote-update-target="applyAllContainer"]')
      if (!container) return

      const rows = group.querySelectorAll('[data-quote-update-target="row"]')
      if (rows.length < 2) {
        container.classList.add('d-none')
        return
      }

      const sourceRow = rows[0]
      const isSourceReady = sourceRow.classList.contains('quote-update-row-complete') || sourceRow.classList.contains('quote-update-row-saved')

      const hasUnsavedTargets = Array.from(rows).slice(1).some(row => !row.classList.contains('quote-update-row-saved'))

      if (isSourceReady && hasUnsavedTargets) {
        container.classList.remove('d-none')
      } else {
        container.classList.add('d-none')
      }
    })
  }



  handleBlur(event) {
    const input = event.target
    const isValid = this.validateInput(input)
    if (isValid && input.value.trim() !== '') {
      const row = input.closest('[data-quote-update-target="row"]')
      const currencySelect = row ? row.querySelector('[name$="[currency]"]') : null
      const currency = currencySelect ? currencySelect.value : 'BRL'
      input.value = this._formatCurrencyValue(input.value.trim(), currency)
      // Trigger checkRow to update indicators and progress bar
      this.checkRow({ target: input })
    } else if (!isValid && input.value.trim() !== '') {
      setTimeout(() => {
        input.focus()
        input.select()
      }, 0)
    }
  }

  handleFocus(event) {
    const input = event.target
    if (input.value === '0,00' || input.value === '0.00') {
      input.value = ''
    }
  }

  handleCurrencyChange(event) {
    const row = event.target.closest('[data-quote-update-target="row"]')
    if (!row) return
    const currency = event.target.value

    const symbols = row.querySelectorAll('[data-quote-update-target="currencySymbol"]')
    symbols.forEach(symbol => {
      symbol.textContent = currency === 'USD' ? 'US$' : 'R$'
    })

    const pricingInputs = row.querySelectorAll('[data-quote-update-target="pricingInput"]')
    pricingInputs.forEach(input => {
      const isValid = this.validateInput(input)
      if (isValid && input.value.trim() !== '') {
        input.value = this._formatCurrencyValue(input.value.trim(), currency)
      }
    })
    // Update completeness visual indicator of the row and overall progress
    const isComplete = this._isRowComplete(row)
    row.classList.toggle('quote-update-row-complete', isComplete)
    row.classList.toggle('quote-update-row-incomplete', !isComplete)
    this.updateProgress()
  }

  _formatCurrencyValue(valStr, currency) {
    // Strip leading currency symbols/spaces
    const cleanVal = valStr.replace(/^[R$\s]+/, '')
    const commaCount = (cleanVal.match(/,/g) || []).length
    const dotCount = (cleanVal.match(/\./g) || []).length

    let normalized = ''
    if (commaCount === 1 && dotCount === 1) {
      if (cleanVal.indexOf(',') > cleanVal.indexOf('.')) {
        // BRL format: 1.400,50 -> 1400.50
        normalized = cleanVal.replace(/\./g, '').replace(',', '.')
      } else {
        // USD format: 1,400.50 -> 1400.50
        normalized = cleanVal.replace(/,/g, '')
      }
    } else if (commaCount === 1) {
      // 1400,50 -> 1400.50
      normalized = cleanVal.replace(',', '.')
    } else if (dotCount === 1) {
      normalized = cleanVal
    } else {
      normalized = cleanVal
    }

    const numberValue = parseFloat(normalized)
    if (isNaN(numberValue)) return valStr

    if (currency === 'BRL') {
      const parts = numberValue.toFixed(2).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.')
      return parts.join(',')
    } else {
      const parts = numberValue.toFixed(2).split('.')
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
      return parts.join('.')
    }
  }

  // -- Private helpers --

  validateInput(input) {
    if (input.disabled) {
      this._clearError(input)
      return true
    }

    const val = input.value.trim()
    if (val === '') {
      this._clearError(input)
      return false
    }

    const validation = this._validateCurrency(val)
    if (!validation.isValid) {
      this._showError(input, validation.message)
      return false
    } else {
      this._clearError(input)
      return true
    }
  }

  _validateCurrency(val) {
    // Strip leading currency symbols/spaces
    const cleanVal = val.replace(/^[R$\s]+/, '')
    if (!/^[0-9.,]+$/.test(cleanVal)) {
      return { isValid: false, message: 'Caracteres inválidos. Use apenas números, ponto e vírgula.' }
    }

    const commaCount = (cleanVal.match(/,/g) || []).length
    const dotCount = (cleanVal.match(/\./g) || []).length

    if (commaCount > 1 || dotCount > 1) {
      return { isValid: false, message: 'Formato inválido. Use apenas um separador decimal.' }
    }

    // Check for ambiguous single separator followed by exactly 3 digits (e.g. 1,400 or 1.400)
    if (commaCount === 1 && dotCount === 0) {
      const parts = cleanVal.split(',')
      if (parts[1].length === 3) {
        return { isValid: false, message: 'Formato ambíguo (ex: 1,400). Use 1400 ou 1400,00.' }
      }
      if (parts[1].length > 2) {
        return { isValid: false, message: 'Use no máximo 2 casas decimais.' }
      }
    }

    if (dotCount === 1 && commaCount === 0) {
      const parts = cleanVal.split('.')
      if (parts[1].length === 3) {
        return { isValid: false, message: 'Formato ambíguo (ex: 1.400). Use 1400 ou 1400,00.' }
      }
      if (parts[1].length > 2) {
        return { isValid: false, message: 'Use no máximo 2 casas decimais.' }
      }
    }

    if (dotCount === 1 && commaCount === 1) {
      const dotIndex = cleanVal.indexOf('.')
      const commaIndex = cleanVal.indexOf(',')
      if (commaIndex > dotIndex) {
        // BRL format: 1.400,00
        const parts = cleanVal.split(',')
        if (parts[1].length > 2) {
          return { isValid: false, message: 'Use no máximo 2 casas decimais.' }
        }
        const thousandsPart = parts[0].split('.')[1]
        if (thousandsPart && thousandsPart.length !== 3) {
          return { isValid: false, message: 'Formato de milhar inválido.' }
        }
      } else {
        // USD format: 1,400.00
        const parts = cleanVal.split('.')
        if (parts[1].length > 2) {
          return { isValid: false, message: 'Use no máximo 2 casas decimais.' }
        }
        const thousandsPart = parts[0].split(',')[1]
        if (thousandsPart && thousandsPart.length !== 3) {
          return { isValid: false, message: 'Formato de milhar inválido.' }
        }
      }
    }

    return { isValid: true }
  }

  _showError(input, message) {
    input.classList.add('is-invalid')
    const container = input.closest('[data-quote-update-target="pricingContainer"]') || input.parentNode
    let errorDiv = container.querySelector('.invalid-feedback')
    if (!errorDiv) {
      errorDiv = document.createElement('div')
      errorDiv.className = 'invalid-feedback d-block mt-1'
      const inputGroup = input.closest('.theme-input-group') || input
      inputGroup.parentNode.insertBefore(errorDiv, inputGroup.nextSibling)
    }
    errorDiv.textContent = message
    errorDiv.style.display = 'block'
  }

  _clearError(input) {
    input.classList.remove('is-invalid')
    const container = input.closest('[data-quote-update-target="pricingContainer"]') || input.parentNode
    const errorDiv = container.querySelector('.invalid-feedback')
    if (errorDiv) {
      errorDiv.remove()
    }
  }

  _isRowComplete(row) {
    const feasibility = this._getFieldValue(row, 'feasibility')
    if (feasibility === 'negative') {
      const mrcInput = row.querySelector('[name$="[mrc]"]')
      const nrcInput = row.querySelector('[name$="[nrc]"]')
      if (mrcInput) this._clearError(mrcInput)
      if (nrcInput) this._clearError(nrcInput)
      return true
    }

    const mrcInput = row.querySelector('[name$="[mrc]"]')
    const nrcInput = row.querySelector('[name$="[nrc]"]')
    const bw = this._getFieldValue(row, 'bandwidth_id')

    if (!mrcInput || !nrcInput) return false

    let isMrcValid = false
    let isNrcValid = false

    const isFormatMrcValid = this.validateInput(mrcInput)
    if (isFormatMrcValid) {
      const val = mrcInput.value.trim().replace(/^[R$\s]+/, '')
      const cleanVal = val.replace(/\./g, '').replace(',', '.')
      const floatVal = parseFloat(cleanVal)
      isMrcValid = !isNaN(floatVal) && floatVal > 0
    }

    const isFormatNrcValid = this.validateInput(nrcInput)
    if (isFormatNrcValid) {
      isNrcValid = true
    }

    return isMrcValid && isNrcValid && bw !== ''
  }

  _getFieldValue(row, fieldSuffix) {
    const field = row.querySelector(`[name$="[${fieldSuffix}]"]`)
    return field ? field.value.trim() : ''
  }

  _setFieldValue(row, fieldSuffix, value) {
    const field = row.querySelector(`[name$="[${fieldSuffix}]"]`)
    if (field) {
      field.value = value
      // Trigger events for any listeners (checkRow listens to input/change)
      field.dispatchEvent(new Event('change', { bubbles: true }))
      field.dispatchEvent(new Event('input', { bubbles: true }))
    }
  }
}
